<template>
  <div class="c-tasks-editor">
    <v-card
      class="mx-auto"
    >
<div class="card-title-wrapper">
  <v-card-title class="d-flex align-center flex-wrap">
    <div class="d-flex align-center">
      Tasks for "{{ selectedProjectName }}"
    </div>
    <v-spacer></v-spacer>
    <div class="d-flex align-start" style="gap: 12px;">
      <div class="d-flex" style="align-items: flex-end;">
        <v-text-field
        dense
        hide-details
        v-model="searchText"
        append-icon="mdi-magnify"
        label="Search tasks"
        single-line
        style="max-width: 200px; min-width: 150px;"
        @input="onSearchChange"
        ></v-text-field>
      </div>
      <div class="d-flex icon-padding" style="align-items: flex-end;">
        <RangeDatePickerMenu
          v-model="dateFilterRange"
          showButtons
          style="max-width: 180px; min-width: 150px;"
          @onOkClick="onDateFilterApply"
          @onClearDates="onDateFilterClear"
        />
      </div>
    </div>
        <v-btn
          small
          color="warning"
          v-if="isIntegratedWithJira && !blockedProject && jiraImportProblems.length"
          class="ml-3"
          @click="showJiraImportProblemsDialog = true"
        >
          Problems after import
        </v-btn>
        <v-btn
          small
          color="primary"
          v-if="isIntegratedWithRedmine && !blockedProject"
          class="ml-3"
          @click="chooseDateFromDialog = true"
        >
          Import reports from Redmine
        </v-btn>
        <v-btn
          small
          color="primary"
          v-if="isIntegratedWithJira && !blockedProject"
          class="ml-3"
          @click="chooseDateFromDialog = true"
        >
          Import reports from Jira
        </v-btn>
        <v-switch
          color="primary"
          :label="openAllFolders ? 'Close folders': 'Open folders'"
          class="d-inline-block ma-0 pa-0 show-all-switcher ml-3"
          hide-details
          v-model="openAllFolders"
        ></v-switch>
        <v-tooltip bottom color="success">
          <template v-slot:activator="{ on, attrs }">
            <v-btn v-bind="attrs" v-on="on" icon color="success" class="ml-2" @click.prevent="showTaskDialog(true)">
              <v-icon>mdi-folder-plus</v-icon>
            </v-btn>
          </template>
          <span>Add folder</span>
        </v-tooltip>
        <v-tooltip bottom color="success">
          <template v-slot:activator="{ on, attrs }">
            <v-btn v-bind="attrs" v-on="on" icon color="success" class="ml-2" @click.prevent="showTaskDialog(false)">
              <v-icon>mdi-file-plus</v-icon>
            </v-btn>
          </template>
          <span>Add task</span>
        </v-tooltip>
      </v-card-title>
      <v-divider></v-divider>
    </div>
      <v-card-text
        v-if="filteredTasks && filteredTasks.length"
        class="px-0"
        :key="`${selectedProjectName}_${selectedProject}_${filterKey}`"
      >
        <DraggableTask
          :nestedLevel="0"
          :tasks="filteredTasks"
          :openFolders="openAllFolders"
          @openFoldersChanged="changeOpenFolders"
        />
      </v-card-text>
      <v-card-text v-else-if="isLoading">
        <v-skeleton-loader
          type="list-item-three-line"
          class="px-4"
        />
      </v-card-text>
      <v-card-text v-else>
         No tasks found for this project
      </v-card-text>
    </v-card>
    <TaskDialog
      v-if="show"
      max-width="440px"
    />
    <ConfirmOrDeniedDialog
      v-model="showDeniedDialog"
      v-if="showDeniedDialog"
      :mode="this.confirmOrDeniedDialogModes.denied"
      :title="'Moving Task'"
      :message="'Tasks or folder status is not the same as the destination folder'"
      @onOkClick="reloadTasks"
      @onCloseClick="reloadTasks"
    />
    <!-- Information dialog -->
    <v-dialog
      v-model="waitForNotification"
      max-width="500"
    >
      <v-card>
        <v-card-title>
          Importing
          <span v-if="isIntegratedWithRedmine" class="px-1">Redmine</span>
          <span v-if="isIntegratedWithJira" class="px-1">JIRA</span>
          reports in progress
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text class="py-4">
          Please wait for notification about finish import
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn small color="primary" @click="waitForNotification = false">Ok</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <!-- /Information dialog -->
    <!-- Jira API Token dialog -->
    <v-dialog
      v-model="jiraApiTokenDialog"
      max-width="500"
    >
      <v-card>
        <v-card-title>
          Jira Access
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text class="py-4">
          Please fill all the fields to get the access to the Jira REST API.
          To get API Token go to the
          <a href="https://id.atlassian.com/manage-profile/security/api-tokens" target="_blank" class="px-1">API Tokens manager</a>
          and create new one. After that paste your token to the "Jira API Token" field.
          <v-text-field
            class="mt-5 pt-3"
            dense
            hide-details
            label="Jira login"
            v-model="jiraApiDefaultLogin"
          ></v-text-field>
          <v-text-field
            class="mt-5 pt-3"
            dense
            hide-details
            label="Jira API Token"
            type="password"
            v-model="jiraApiToken"
          ></v-text-field>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-btn outlined small color="primary" @click="jiraApiTokenDialog = false">Cancel</v-btn>
          <v-spacer></v-spacer>
          <v-btn
            small
            color="primary"
            :disabled="!jiraApiDefaultLogin || !jiraApiToken"
            @click="saveJiraRestApiAccessInformation">Save</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <!-- /Jira API Token dialog -->
    <!-- Choose date for import from redmine -->
    <v-dialog
      v-model="chooseDateFromDialog"
      max-width="500"
      dense
    >
      <v-card>
        <v-card-title>
          Choose dates for import
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text class="py-4">
          <RangeDatePickerMenu
            v-model="dateRange"
            label="Period"
            showButtons
          />
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-btn small outlined color="primary" @click="chooseDateFromDialog = false">Cancel</v-btn>
          <v-spacer></v-spacer>
          <v-btn small color="primary" @click="importReports">Ok</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <!-- /Choose date for import from redmine -->
    <!-- Jira Import Problems -->
    <JiraImportProblemsListDialog
      v-model="showJiraImportProblemsDialog"
      :problems="jiraImportProblems"
      @resolveProblem="resolveProblem"
      @ignoreProblem="ignoreProblem"
    />
    <!-- /Jira Import Problems -->
  </div>
</template>

<script>
export default {
  name: 'TasksEditor',
  components: {
    ConfirmOrDeniedDialog: () => import('@/components/global/ConfirmOrDeniedDialog.vue'),
    DraggableTask: () => import('@/components/tasks_editor/DraggableTask.vue'),
    TaskDialog: () => import('@/components/tasks_editor/TaskDialog.vue'),
    RangeDatePickerMenu: () => import('@/components/global/RangeDatePickerMenu.vue'),
    JiraImportProblemsListDialog: () => import('@/components/tasks_editor/JiraImportProblemsListDialog.vue')
  },
  data () {
    return {
      showJiraImportProblemsDialog: false,
      currentJiraIntegrationID: null,
      openAllFolders: false,
      jiraApiTokenDialog: false,
      jiraApiDefaultLogin: null,
      jiraApiToken: null,
      jiraImportProblems: [],
      chooseDateFromDialog: false,
      isIntegratedWithRedmine: false,
      isIntegratedWithJira: false,
      waitForNotification: false,
      minDate: null,
      datePickerMenu: false,
      dates: [new Date().toISOString().substring(0, 10), new Date().toISOString().substring(0, 10)],
      dateFrom: new Date().toISOString().substring(0, 10),
      searchText: '',
      dateFilterRange: [],
      filterKey: 0,
      originalTasks: []
    }
  },
  computed: {
    confirmOrDeniedDialogModes () {
      return this.$store.getters.getConfirmOrDeniedDialogModes
    },
    showDeniedDialog: {
      get () {
        return this.$store.getters.getShowConfirmDeniedDialog
      },
      set (val) {
        this.$store.dispatch('showConfirmDeniedDialog', { show: val })
      }
    },
    dateRange: {
      get () {
        return this.dates
      },
      set (value) {
        this.dates = value.sort((a, b) => {
          const dA = new Date(a)
          const dB = new Date(b)
          if (+dA > +dB) return 1
          if (+dA < +dB) return -1
          return 0
        })
      }
    },
    blockedProject () {
      return this.$store.getters.getBlockedProject
    },
    showDialog () {
      return this.$store.getters.showDetailsDialog
    },
    show () {
      return this.$store.getters.showTaskDialog
    },
    tasks () {
      return this.$store.getters.getCurrentTasksList
    },
    filteredTasks () {
      if (!this.originalTasks.length) return this.tasks

      let filtered = [...this.originalTasks]

      if (this.searchText) {
        const searchLower = this.searchText.toLowerCase()
        filtered = this.filterTasksBySearch(filtered, searchLower)
      }

      if (this.dateFilterRange && this.dateFilterRange.length === 2) {
        const startDate = new Date(this.dateFilterRange[0])
        const endDate = new Date(this.dateFilterRange[1])
        filtered = this.filterTasksByDate(filtered, startDate, endDate)
      }

      return filtered
    },
    selectedProject () {
      return Array.isArray(this.$store.getters.getSelectedProject)
        ? this.$store.getters.getSelectedProject[0]
        : this.$store.getters.getSelectedProject
    },
    selectedProjectName () {
      return this.$store.getters.getSelectedProjectName
    },
    user () {
      return this.$store.getters.getUser
    },
    updateTasks () {
      return this.$store.getters.updateTasks
    },
    isLoading () {
      return this.$store.getters.getIsLoading
    }
  },
  watch: {
    tasks: {
      immediate: true,
      handler (newTasks) {
        if (newTasks && newTasks.length) {
          this.originalTasks = this.deepCloneTasks(newTasks)
        }
      }
    }
  },
  mounted () {
    this.checkForIntegrationWithRedmine()
    this.checkForIntegrationWithJira()
    this.getTasks()
    this.lastSummaryDate()
    if (this.updateTasks) {
      this.$store.dispatch('updateTasks', { update: false })
    }
    this.$store.dispatch('toggleTaskToSelected', {
      taskId: null,
      action: 'reset'
    })
  },
  methods: {
    deepCloneTasks (tasks) {
      if (typeof window !== 'undefined' && typeof window.structuredClone === 'function') {
        return window.structuredClone(tasks)
      } else {
        // fallback for old browsers
        return JSON.parse(JSON.stringify(tasks))
      }
    },

    filterTasksBySearch (tasks, searchText) {
      return tasks.filter(task => {
        const matches = task.Name.toLowerCase().includes(searchText)

        if (task.Items && task.Items.length) {
          const filteredChildren = this.filterTasksBySearch(task.Items, searchText)
          if (filteredChildren.length > 0) {
            task.Items = filteredChildren
            return true
          }
        }

        return matches
      })
    },

    filterTasksByDate (tasks, startDate, endDate) {
      return tasks.filter(task => {
        const taskDate = new Date(task.Start_Date)
        const dateInRange = taskDate >= startDate && taskDate <= endDate

        if (task.Items && task.Items.length) {
          const filteredChildren = this.filterTasksByDate(task.Items, startDate, endDate)
          if (filteredChildren.length > 0) {
            task.Items = filteredChildren
            return true
          }
        }

        return dateInRange
      })
    },

    onSearchChange () {
      this.filterKey++
    },

    onDateFilterApply () {
      this.filterKey++
    },

    onDateFilterClear () {
      this.dateFilterRange = []
      this.filterKey++
    },

    reloadTasks () {
      this.searchText = ''
      this.dateFilterRange = []
      this.$store.dispatch('updateTasks', { update: true })
    },
    ignoreProblem (e) {
      this.axios.delete(`/api/integrations/ignore-jira-import-problem/${e}`, {
        headers: {
          Authorization: 'Bearer ' + this.user.token
        }
      }).then(res => {
        this.getJiraImportProblems()
      }).catch(error => {
        this.$store.dispatch('setError', { errorMsg: error.message + ' - ' + error.response.data })
      })
    },
    resolveProblem (e) {
      this.axios.post('/api/integrations/resolve-jira-import-problem',
        {
          Integration_ID: this.currentJiraIntegrationI,
          Root_Folder_Name: e.Problem_Data.Root_Folder_Name,
          Project_ID: e.Project_ID,
          Assignee_Display_Name: e.Problem_Data.Assignee_Display_Name,
          Jira_Issue_Name: e.Problem_Data.Jira_Issue_Name,
          Jira_Issue_Key: e.Problem_Data.Jira_Issue_Key,
          Jira_Issue_ID: e.Problem_Data.Jira_Issue_ID,
          Jira_Report_ID: e.Problem_Data.Jira_Report_ID,
          Start_Date: e.Problem_Data.Start_Date,
          Real_Dur: e.Problem_Data.Real_Dur,
          Report_Dur: e.Problem_Data.Report_Dur,
          Story_Points: e.Problem_Data.Story_Points,
          Timespent: e.Problem_Data.Timespent,
          Status_Name: e.Problem_Data.Status_Name,
          Description: e.Problem_Data.Description,
          Problem_ID: e.Problem_ID
        },
        {
          headers: {
            Authorization: 'Bearer ' + this.user.token
          }
        }).then(res => {
        this.$store.dispatch('setSuccess', { successMsg: 'Problem successfully resolved' })
        this.$store.dispatch('updateTasks', { update: true })
      }).catch(error => {
        this.$store.dispatch('setError', { errorMsg: error.message + ' - ' + error.response.data })
      })
    },
    lastSummaryDate () {
      this.axios.get('/api/last_summary_date/' + this.selectedProject, {
        headers: {
          Authorization: 'Bearer ' + this.user.token
        }
      }).then(response => {
        if (response.data[0]) {
          this.minDate = new Date(+(new Date(response.data[0].To_Date)) + 86400000).toISOString().substring(0, 10)
        } else {
          this.minDate = null
        }
      }).catch(error => {
        this.$store.dispatch('setError', { errorMsg: error.message + ' - ' + error.response.data })
      })
    },
    changeOpenFolders (e) {
      this.openAllFolders = e
    },
    showTaskDialog (isFolder) {
      const defaultTask = {
        Assigned_To: null,
        Budget_SubType_ID: null,
        Description: '',
        Estimate: 0,
        Is_Folder: isFolder,
        Name: '',
        Parent_Task_ID: null,
        Project_ID: this.selectedProject,
        Reported: 0,
        Show_IN_Report: true,
        Start_Date: new Date().toISOString().substr(0, 10),
        Status: 'Open',
        Task_ID: null,
        Redmine_Issue_ID: null,
        TestTrack_ID: null,
        root_task_id: null
      }
      this.$store.dispatch('setCurrentTaskData', { taskData: defaultTask })
      this.$store.dispatch('showTaskDialog', { show: true })
    },
    saveEstRepData (items) {
      for (const key in items) {
        this.$store.dispatch('addEstRepForTask', {
          taskId: items[key].Task_ID,
          est: items[key].Estimate,
          rep: items[key].Is_Folder ? items[key].Reported : items[key].Total_Reported
        })
        if (items[key].Items) {
          this.saveEstRepData(items[key].Items)
        }
      }
    },
    importReports () {
      if (this.isIntegratedWithRedmine) {
        this.importReportsFromRedmine()
      }
      if (this.isIntegratedWithJira) {
        this.importReportsFromJira()
      }
    },
    saveJiraRestApiAccessInformation () {
      this.jiraApiTokenDialog = false
      this.axios.post('/api/integrations/jira-access', {
        Employee_ID: this.user.employee_id,
        Jira_Login: this.jiraApiDefaultLogin,
        API_Token: this.jiraApiToken,
        Project_ID: this.selectedProject
      },
      {
        headers: {
          Authorization: 'Bearer ' + this.user.token
        }
      }).then(res => {
        this.jiraApiTokenDialog = false
        this.chooseDateFromDialog = true
        this.$store.dispatch('setSuccess', { successMsg: 'Your access information successfully saved' })
      }).catch(error => {
        this.$store.dispatch('setError', { errorMsg: error.message + ' - ' + error.response.data })
      })
    },
    importReportsFromJira () {
      this.chooseDateFromDialog = false
      this.axios.post('/api/integrations/import-jira-reports', {
        Project_ID: this.selectedProject,
        Employee_ID: this.user.employee_id,
        Date_From: this.dateRange[0],
        Date_To: this.dateRange[1],
        Min_Date: this.minDate,
        Send_Notif_For: this.user.employee_id
      },
      {
        headers: {
          Authorization: 'Bearer ' + this.user.token
        }
      }).then(res => {
        if (res.data === 'no_api_token') {
          this.jiraApiDefaultLogin = this.user.e_mail
          this.jiraApiTokenDialog = true
        } else {
          this.waitForNotification = true
          this.$store.dispatch('blockProject', { projectID: this.selectedProject })
        }
      }).catch(error => {
        this.$store.dispatch('setError', { errorMsg: error.message + ' - ' + error.response.data })
      })
    },
    importReportsFromRedmine () {
      this.chooseDateFromDialog = false
      this.axios.post('/api/integrations/redmine-reports', {
        Project_ID: this.selectedProject,
        Send_Notif_For: this.user.employee_id,
        Date_From: this.dateRange[0],
        Date_To: this.dateRange[1]
      }).then(res => {
        this.waitForNotification = true
        this.$store.dispatch('blockProject', { projectID: this.selectedProject })
      }).catch(error => {
        this.$store.dispatch('setError', { errorMsg: error.message + ' - ' + error.response.data })
      })
    },
    checkForIntegrationWithRedmine () {
      setTimeout(() => {
        this.axios.get(`/api/integrations/redmine/${this.selectedProject}`, {
          headers: {
            Authorization: 'Bearer ' + this.user.token
          }
        }).then(res => {
          if (res.data.length) this.isIntegratedWithRedmine = true
        }).catch(() => {
          this.isIntegratedWithRedmine = false
        })
      })
    },
    checkForIntegrationWithJira () {
      setTimeout(() => {
        this.axios.get(`/api/integrations/jira/${this.selectedProject}`, {
          headers: {
            Authorization: 'Bearer ' + this.user.token
          }
        }).then(res => {
          if (res.data.length) {
            this.isIntegratedWithJira = true
            this.currentJiraIntegrationID = res.data[0].Integration_ID
          }
          this.getJiraImportProblems()
        }).catch(() => {
          this.isIntegratedWithJira = false
        })
      })
    },
    getJiraImportProblems () {
      this.axios.get(`/api/integrations/jira-import-problems/${this.selectedProject}`, {
        headers: {
          Authorization: 'Bearer ' + this.user.token
        }
      }).then(res => {
        this.jiraImportProblems = res.data
      }).catch(() => {
        this.jiraImportProblems = []
      })
    },
    getTasks () {
      this.$store.dispatch('setIsLoading', { setIsLoading: true })
      this.axios.get(`/api/tasks/${this.selectedProject}`, {
        headers: { Authorization: `Bearer ${this.user.token}` }
      })
        .then(response => {
          const [tasks, users] = response.data
          this.$store.dispatch('setCurrentTasksList', { tasksList: tasks })
          if (users?.length) {
            this.$store.dispatch('setUsersOnProject', { userIds: users })
          } else {
            this.$store.dispatch('setUsersOnProject', { userIds: [] })
          }
          this.saveEstRepData(tasks)
        })
        .catch(error => {
          const msg = error.response?.data || error.message
          this.$store.dispatch('setError', { errorMsg: msg })
        })
        .finally(() => {
          this.$store.dispatch('setIsLoading', { setIsLoading: false })
        })
    }
  }
}
</script>

<style lang="scss">
  .show-all-switcher {
    vertical-align: middle;
  }
  .card-title-wrapper {
    position: sticky;
    top: 60px;
    z-index: 5;
    background-color: white;
  }
  .theme--dark .card-title-wrapper {
    background-color: rgba(#121212, 1);
  }

  .icon-padding .v-input__icon {
    padding-bottom: 6px !important;
  }
</style>
